import React, { useEffect, useRef, useState } from 'react';
import { Modal, Slider, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    Offset,
    Size,
    centeredOffset,
    clampOffset,
    coverScale,
    cropImageToBlob,
    croppedFileName,
    rescaleOffset,
    scaledSize,
    sourceRect,
} from '../utils/bannerCropUtils';

interface BannerCropModalProps {
    open: boolean;
    /** 待裁剪的原图；关闭后置空 */
    file: File | null;
    /** 导出尺寸（750×300） */
    output: Size;
    onCancel: () => void;
    onCropped: (file: File) => void;
}

/** 裁剪框显示尺寸：固定 2.5:1 */
const BOX: Size = { width: 500, height: 200 };
/** 裁剪框在容器里的内边距，露出一圈被遮罩的图片便于拖动 */
const PADDING: Offset = { x: 30, y: 30 };
const CONTAINER: Size = { width: BOX.width + PADDING.x * 2, height: BOX.height + PADDING.y * 2 };
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.01;

/**
 * 简单裁剪弹窗：固定 2.5:1 裁剪框，图片可拖动、滑块缩放（1~4 倍，最小倍数即铺满裁剪框），
 * 确认后用 canvas 导出 output 尺寸的 png 交回调用方上传。不依赖第三方裁剪库。
 */
const BannerCropModal: React.FC<BannerCropModalProps> = ({ open, file, output, onCancel, onCropped }) => {
    const { t } = useTranslation();
    const imageRef = useRef<HTMLImageElement>(null);
    const [src, setSrc] = useState<string | null>(null);
    const [natural, setNatural] = useState<Size | null>(null);
    const [zoom, setZoom] = useState(ZOOM_MIN);
    const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
    const [exporting, setExporting] = useState(false);
    const drag = useRef<{ startX: number; startY: number; origin: Offset } | null>(null);

    // 打开时读图，关闭时释放 objectURL
    useEffect(() => {
        if (!open || !file) {
            setSrc(null);
            setNatural(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setSrc(url);
        setZoom(ZOOM_MIN);
        return () => URL.revokeObjectURL(url);
    }, [open, file]);

    const baseScale = natural ? coverScale(natural, BOX) : 1;
    const scale = baseScale * zoom;
    const scaled = natural ? scaledSize(natural, scale) : BOX;

    const handleLoaded = () => {
        const img = imageRef.current;
        if (!img) return;
        const size = { width: img.naturalWidth, height: img.naturalHeight };
        setNatural(size);
        setOffset(centeredOffset(scaledSize(size, coverScale(size, BOX)), BOX));
    };

    const handleZoom = (next: number) => {
        if (!natural) return;
        const nextScale = baseScale * next;
        const moved = rescaleOffset(offset, BOX, scale, nextScale);
        setZoom(next);
        setOffset(clampOffset(moved, scaledSize(natural, nextScale), BOX));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        drag.current = { startX: e.clientX, startY: e.clientY, origin: offset };
    };

    useEffect(() => {
        if (!open) return undefined;
        const onMove = (e: MouseEvent) => {
            if (!drag.current || !natural) return;
            const next = {
                x: drag.current.origin.x + (e.clientX - drag.current.startX),
                y: drag.current.origin.y + (e.clientY - drag.current.startY),
            };
            setOffset(clampOffset(next, scaledSize(natural, scale), BOX));
        };
        const onUp = () => {
            drag.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [open, natural, scale]);

    const handleOk = async () => {
        const img = imageRef.current;
        if (!img || !file || !natural) return;
        setExporting(true);
        try {
            const blob = await cropImageToBlob(img, sourceRect(offset, scale, BOX), output);
            onCropped(new File([blob], croppedFileName(file.name), { type: 'image/png' }));
        } catch (e) {
            console.error('Banner crop failed', e);
            message.error(t('driverOps.activity.banner.cropFailed'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <Modal
            open={open}
            title={t('driverOps.activity.banner.cropTitle')}
            width={CONTAINER.width + 48}
            onCancel={onCancel}
            onOk={() => void handleOk()}
            okButtonProps={{ disabled: !natural, loading: exporting }}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
            destroyOnClose
            maskClosable={false}
        >
            <div className="text-[12.5px] text-[#8C8C8C] mb-3">
                {t('driverOps.activity.banner.cropHint', { width: output.width, height: output.height })}
            </div>
            <div
                className="relative overflow-hidden bg-[#2B2B2B] rounded-[6px] select-none"
                style={{ width: CONTAINER.width, height: CONTAINER.height, cursor: natural ? 'grab' : 'default' }}
                onMouseDown={handleMouseDown}
                data-testid="banner-crop-stage"
            >
                {src && (
                    <img
                        ref={imageRef}
                        src={src}
                        alt="crop"
                        draggable={false}
                        onLoad={handleLoaded}
                        className="absolute max-w-none"
                        style={{
                            left: PADDING.x + offset.x,
                            top: PADDING.y + offset.y,
                            width: scaled.width,
                            height: scaled.height,
                            visibility: natural ? 'visible' : 'hidden',
                        }}
                    />
                )}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        border: '1px solid rgba(255, 255, 255, 0.9)',
                        left: PADDING.x,
                        top: PADDING.y,
                        width: BOX.width,
                        height: BOX.height,
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
                    }}
                />
            </div>
            <div className="flex items-center gap-3 mt-3">
                <span className="text-[12.5px] text-[#8C8C8C] shrink-0">1x</span>
                <Slider
                    className="flex-1"
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    value={zoom}
                    disabled={!natural}
                    onChange={handleZoom}
                    tooltip={{ formatter: (v) => `${Number(v ?? 1).toFixed(2)}x` }}
                />
                <span className="text-[12.5px] text-[#8C8C8C] shrink-0">{ZOOM_MAX}x</span>
            </div>
        </Modal>
    );
};

export default BannerCropModal;
